import React from 'react';
import Navigation from '@/components/navigation';
import { Card } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import WavyBadge from '@/components/WavyBadge';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { resolveDailyTargetMl, resolveFrequencyGoal } from '@/lib/hydrationGoals';
import { buildWeekAnalytics, computeOrgRanks } from '@/lib/weekAnalytics';
import { buildYesterdayBadgeRows } from '@/lib/yesterdayBadges';
import FrequencyBadgeImage from '@/components/FrequencyBadgeImage';
import VolumeBadgeImage from '@/components/VolumeBadgeImage';
import { format, subDays } from 'date-fns';
import BrandHeader from '@/components/BrandHeader';
import { getDisplayName } from '@/lib/displayName';

const Analytics = () => {
  const { user } = useAuth();
  const today = new Date().toLocaleDateString('en-CA');
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const [carouselApi, setCarouselApi] = React.useState<any>(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [activeDayIdx, setActiveDayIdx] = React.useState(6);

  React.useEffect(() => {
    if (!carouselApi) return;
    setCurrentSlide(carouselApi.selectedScrollSnap());
    carouselApi.on('select', () => setCurrentSlide(carouselApi.selectedScrollSnap()));
  }, [carouselApi]);

  const { data: userProfile } = useQuery<any>({
    queryKey: ['/api/profile'],
    retry: false,
  });
  const nickname = getDisplayName({
    ...(user as any),
    avatarName: userProfile?.avatarName || (user as any)?.profile?.avatarName,
  });

  const activeDays: string[] = userProfile?.activeDays || [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ];

  const weekStart = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toLocaleDateString('en-CA');
  }, []);

  const { data: weeklyStats } = useQuery<any[]>({
    queryKey: ['/api/stats/weekly', { startDate: weekStart, endDate: today }],
    retry: false,
  });

  const { data: yesterdayStats } = useQuery<any>({
    queryKey: ['/api/stats/daily', { date: yesterday }],
    retry: false,
  });

  const { data: yesterdayTracking } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: yesterday }],
    retry: false,
  });

  const { data: leaderboardData } = useQuery<any[]>({
    queryKey: ['/api/leaderboard'],
    retry: false,
  });

  const fallbackTarget = resolveDailyTargetMl(userProfile);

  const week = React.useMemo(
    () => buildWeekAnalytics(activeDays, weeklyStats || [], fallbackTarget),
    [activeDays, weeklyStats, fallbackTarget]
  );

  React.useEffect(() => {
    setActiveDayIdx(Math.max(0, week.days.length - 1));
  }, [week.days.length]);

  const yesterdayRows = React.useMemo(
    () =>
      buildYesterdayBadgeRows({
        totalVolume: yesterdayStats?.totalVolume || 0,
        targetVolume: yesterdayStats?.targetVolume || fallbackTarget,
        frequencyScore: yesterdayStats?.frequencyScore,
        missCount: yesterdayStats?.missCount ?? yesterdayTracking?.missCount ?? 0,
        successfulIntakes:
          yesterdayStats?.successfulReminders ?? yesterdayTracking?.successfulIntakes,
        expectedReminders: resolveFrequencyGoal(userProfile),
        totalReminders: yesterdayStats?.totalReminders,
        volumeBadgeName: yesterdayStats?.volumeBadgeName,
        frequencyBadgeName: yesterdayStats?.frequencyBadgeName,
        volumeBadgePercent: yesterdayStats?.volumeBadgePercent,
        badgesFinalized: yesterdayStats?.badgesFinalized,
        activeHoursStart: userProfile?.activeHoursStart,
        activeHoursEnd: userProfile?.activeHoursEnd,
        reminderInterval: userProfile?.reminderInterval,
      }),
    [yesterdayStats, yesterdayTracking, userProfile, fallbackTarget]
  );

  const orgRanks = React.useMemo(() => {
    const list = Array.isArray(leaderboardData)
      ? leaderboardData
      : (leaderboardData as any)?.leaderboard || [];
    return computeOrgRanks(list, (user as any)?.id);
  }, [leaderboardData, user]);

  const consistency = Math.round(
    ((week.volumeHits + week.frequencyHits) / Math.max(week.days.length * 2, 1)) * 100
  );

  const chartDays = week.days;
  const maxV = Math.max(0.5, ...chartDays.map((d) => Math.max(d.intakeL, d.goalL)), 0.1);
  const W = 340;
  const H = 200;
  const pad = { l: 28, r: 24, t: 24, b: 32 };
  const xs = chartDays.map(
    (_, i) => pad.l + (i * (W - pad.l - pad.r)) / Math.max(chartDays.length - 1, 1)
  );
  const intakeYs = chartDays.map((p) => pad.t + (1 - p.intakeL / maxV) * (H - pad.t - pad.b));
  const goalYs = chartDays.map((p) => pad.t + (1 - p.goalL / maxV) * (H - pad.t - pad.b));
  const pathD = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${intakeYs[i]}`).join(' ');
  const goalPathD = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${goalYs[i]}`).join(' ');
  const highlightIdx = activeDayIdx;
  const selected = chartDays[highlightIdx];

  const volumePercentile =
    orgRanks.volumeRank && orgRanks.totalMembers > 0
      ? Math.round(
          ((orgRanks.totalMembers - orgRanks.volumeRank + 1) / orgRanks.totalMembers) * 100
        )
      : 0;
  const freqPercentile =
    orgRanks.frequencyRank && orgRanks.totalMembers > 0
      ? Math.round(
          ((orgRanks.totalMembers - orgRanks.frequencyRank + 1) / orgRanks.totalMembers) * 100
        )
      : 0;

  return (
    <div className="min-h-screen pb-28 bg-cream">
      <div className="max-w-md mx-auto px-5 py-5 space-y-5">
        <BrandHeader title="Pulse" nickname={nickname} />

        <Card className="bg-cream border border-deep-100 rounded-3xl p-5 shadow-sm">
          <Carousel setApi={setCarouselApi} className="w-full">
            <CarouselContent>
              <CarouselItem>
                <div>
                  <h2 className="text-lg font-bold text-deep-800 mb-4">Your progress</h2>
                  <div className="relative h-[300px]">
                    <StatBlob
                      className="absolute top-0 left-0 w-[48%] h-[40%]"
                      value={`${week.weeklyAverageL}L`}
                      label="WEEKLY AVG"
                    />
                    <StatBlob
                      className="absolute top-2 right-0 w-[48%] h-[40%]"
                      value={week.bestDayLabel}
                      label="BEST DAY"
                    />
                    <StatBlob
                      className="absolute bottom-0 left-0 w-[48%] h-[45%]"
                      value={`${week.volumeHits}/${week.days.length}`}
                      label="VOL GOAL HIT"
                    />
                    <StatBlob
                      className="absolute bottom-2 right-0 w-[52%] h-[48%]"
                      value={`${week.frequencyHits}/${week.days.length}`}
                      label="FREQ GOAL HIT"
                      big
                    />
                  </div>
                </div>
              </CarouselItem>

              <CarouselItem>
                <div>
                  <h2 className="text-lg font-bold text-deep-800 mb-3">Yesterday&apos;s Badges</h2>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {yesterdayRows.map((row) => (
                      <div
                        key={row.title}
                        className="bg-lilac-200/50 rounded-2xl p-3 border border-lilac-300"
                      >
                        <div className="flex items-start gap-3">
                          {row.imageSrc ? (
                            row.imageSrc.includes('/frequency/') ? (
                              <FrequencyBadgeImage src={row.imageSrc} alt={row.title} size="sm" />
                            ) : (
                              <VolumeBadgeImage src={row.imageSrc} alt={row.title} size="sm" />
                            )
                          ) : (
                            <span className="text-2xl">{row.emoji}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-deep-800 text-sm">{row.title}</p>
                            <p className="text-xs text-deep-500 mt-0.5">{row.subtitle}</p>
                            <p className="text-xs text-purple-700 font-medium mt-2 leading-snug">
                              → {row.action}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>

          <div className="mt-3 text-center">
            <p className="text-xs text-deep-500 mb-2">Swipe For Progress</p>
            <div className="flex justify-center gap-1.5">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${currentSlide === i ? 'bg-purple-500' : 'bg-deep-100'}`}
                />
              ))}
            </div>
          </div>
        </Card>

        <div>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-deep-800">Weekly Progress</h2>
              <p className="text-sm text-deep-500">Last {week.days.length} active days</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-deep-800 leading-none">{consistency}%</p>
              <p className="text-sm font-semibold text-deep-700 mt-1">consistency</p>
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto scrollbar-none">
            {chartDays.map((d, i) => (
              <WavyBadge
                key={d.date}
                size="sm"
                filled={i === activeDayIdx}
                onClick={() => setActiveDayIdx(i)}
                className="shrink-0"
              >
                {d.dayLetter}
              </WavyBadge>
            ))}
          </div>
        </div>

        <Card className="bg-cream border-none shadow-none p-0">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
            <text x={pad.l} y={14} className="fill-deep-400" style={{ fontSize: 10 }}>
              — goal
            </text>
            <text x={pad.l + 36} y={14} className="fill-purple-600" style={{ fontSize: 10 }}>
              — intake
            </text>
            <path
              d={goalPathD}
              stroke="hsl(var(--deep-400))"
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="4 4"
              strokeLinecap="round"
            />
            {chartDays.map((d, i) => {
              if (d.deficitL <= 0) return null;
              const top = intakeYs[i];
              const bottom = goalYs[i];
              return (
                <line
                  key={`gap-${d.date}`}
                  x1={xs[i]}
                  y1={top}
                  x2={xs[i]}
                  y2={bottom}
                  stroke="hsl(var(--destructive))"
                  strokeWidth="2"
                  opacity="0.35"
                />
              );
            })}
            <path
              d={pathD}
              stroke="hsl(var(--purple-500))"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            />
            {xs.map((x, i) => (
              <circle
                key={i}
                cx={x}
                cy={intakeYs[i]}
                r={i === highlightIdx ? 10 : 7}
                fill={i === highlightIdx ? 'hsl(var(--lime-500))' : 'hsl(var(--purple-500))'}
                stroke="white"
                strokeWidth="2"
              />
            ))}
            {selected &&
              (() => {
                const tipW = 68;
                const tipH = 36;
                const tipX = Math.min(Math.max(xs[highlightIdx] - tipW / 2, 2), W - tipW - 2);
                const tipY = Math.min(
                  Math.max(intakeYs[highlightIdx] - tipH - 12, 2),
                  H - tipH - 2
                );
                return (
                  <g>
                    <rect
                      x={tipX}
                      y={tipY}
                      width={tipW}
                      height={tipH}
                      rx="10"
                      fill="hsl(var(--deep-800))"
                    />
                    <text
                      x={tipX + tipW / 2}
                      y={tipY + 14}
                      textAnchor="middle"
                      className="fill-white font-bold"
                      style={{ fontSize: 10 }}
                    >
                      {selected.intakeL}L
                    </text>
                    <text
                      x={tipX + tipW / 2}
                      y={tipY + 28}
                      textAnchor="middle"
                      className="fill-lilac-200"
                      style={{ fontSize: 9 }}
                    >
                      gap {selected.deficitL}L
                    </text>
                  </g>
                );
              })()}
            {chartDays.map((p, i) => (
              <text
                key={p.date}
                x={xs[i]}
                y={H - 8}
                textAnchor="middle"
                className={i === highlightIdx ? 'fill-deep-800 font-bold' : 'fill-deep-300'}
                style={{ fontSize: 11 }}
              >
                {p.dayLetter}
              </text>
            ))}
          </svg>
        </Card>

        <div>
          <h2 className="text-xl font-bold text-deep-800">Squad Analytics</h2>
          <p className="text-sm text-deep-500 mb-3">
            Organisation ranking — volume &amp; frequency
          </p>
          <Card className="bg-cream border-2 border-dashed border-deep-200 rounded-2xl p-5">
            <div className="space-y-5">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-deep-700 tracking-wider">
                    VOLUME RANK
                  </span>
                  <span className="text-lg font-bold text-deep-800">
                    {orgRanks.volumeRank ? `#${orgRanks.volumeRank}` : '—'}
                    {volumePercentile > 0 ? ` · Top ${volumePercentile}%` : ''}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-white overflow-hidden">
                  <div
                    className="h-full rounded-full bg-purple-500"
                    style={{
                      width: `${Math.min(100, orgRanks.userVolumeScore)}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-deep-500 mt-1">
                  You {Math.round(orgRanks.userVolumeScore)} · Org avg{' '}
                  {Math.round(orgRanks.orgVolumeScore)}
                </p>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-deep-700 tracking-wider">
                    FREQUENCY RANK
                  </span>
                  <span className="text-lg font-bold text-deep-800">
                    {orgRanks.frequencyRank ? `#${orgRanks.frequencyRank}` : '—'}
                    {freqPercentile > 0 ? ` · Top ${freqPercentile}%` : ''}
                  </span>
                </div>
                <div className="h-3 rounded-full bg-white overflow-hidden">
                  <div
                    className="h-full rounded-full bg-deep-700"
                    style={{
                      width: `${Math.min(100, orgRanks.userFrequencyScore)}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-deep-500 mt-1">
                  You {Math.round(orgRanks.userFrequencyScore)} · Org avg{' '}
                  {Math.round(orgRanks.orgFrequencyScore)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Navigation />
    </div>
  );
};

const StatBlob = ({
  className,
  value,
  label,
  big,
}: {
  className?: string;
  value: string;
  label: string;
  big?: boolean;
}) => (
  <div className={className}>
    <div className="relative w-full h-full">
      <svg
        viewBox="0 0 200 160"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
      >
        <path
          d="M40,20 C70,5 130,0 165,25 C195,50 198,100 175,135 C150,170 80,165 45,145 C10,125 5,75 15,50 C20,35 28,28 40,20 Z"
          fill="hsl(var(--lilac-300))"
        />
      </svg>
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-3">
        <p
          className={`font-bold text-deep-800 break-words ${big ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'}`}
        >
          {value}
        </p>
        <p className="text-[10px] sm:text-[11px] tracking-wide text-deep-500 font-medium mt-1 line-clamp-2 break-words">
          {label}
        </p>
      </div>
    </div>
  </div>
);

export default Analytics;
